import React from "react";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import { IconButton, Box, Typography, Tooltip, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMutation, useQuery } from "@tanstack/react-query";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import UserRow from "./parts/UserRow";
import {
  OrganizationUser,
  SetUserActivePayload,
} from "../../services/user-management.service";
import { userManagementService } from "../../services";
import { Column } from "../../../common/Table/types";
import TableView from "../../../common/Table";
import { formatDateAndTime } from "../../../../utils/formatDate";
import { RoleName } from "../../../common/enums/RoleName";
import ConfirmUserDeleteDialog from "../ConfirmUserDeleteDialog";
import ManageRolesDialog from "../ManageRolesDialog";
import ButtonView from "../../../common/Button";
import RoleBasedAccess from "../../../common/RoleBasedAccess";

const columns: Column[] = [
  { id: "email", label: "Email", minWidth: 100 },
  { id: "first_name", label: "First Name", minWidth: 100 },
  { id: "last_name", label: "Last Name", minWidth: 100 },
  { id: "is_active", label: "Is Active", minWidth: 100 },
  { id: "roles", label: "Roles", minWidth: 100 },
  { id: "created_at", label: "Created At", minWidth: 100 },
  { id: "delete", label: "Delete", minWidth: 100 },
];

const SKELETON_ROWS = 6;
const colWidths = ["30%", "12%", "12%", "10%", "14%", "14%", "8%"];

function TableSkeletonLoader() {
  return (
    <Table>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
          {colWidths.map((w) => (
            <TableCell key={w} sx={{ py: 1.25, px: 2, width: w }}>
              <Skeleton variant="text" width="60%" height={14} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={rowIdx} sx={{ "&:last-child td": { border: 0 } }}>
            {/* Email */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="80%" height={14} />
            </TableCell>
            {/* First Name */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="65%" height={14} />
            </TableCell>
            {/* Last Name */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="65%" height={14} />
            </TableCell>
            {/* Is Active chip */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton
                variant="rounded"
                width={52}
                height={22}
                sx={{ borderRadius: "12px" }}
              />
            </TableCell>
            {/* Roles chips */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Skeleton
                  variant="rounded"
                  width={48}
                  height={22}
                  sx={{ borderRadius: "12px" }}
                />
                <Skeleton
                  variant="rounded"
                  width={40}
                  height={22}
                  sx={{ borderRadius: "12px" }}
                />
              </Box>
            </TableCell>
            {/* Created At */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="75%" height={14} />
            </TableCell>
            {/* Delete action */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="circular" width={28} height={28} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function createData(
  id: string,
  email: string,
  first_name: string,
  last_name: string,
  is_active: boolean,
  created_at: string,
  roles: RoleName[],
): any {
  return { id, email, first_name, last_name, is_active, created_at, roles };
}

interface Props {
  handleOpenAddUserDialog: () => void;
}

export default function UsersList({ handleOpenAddUserDialog }: Props) {
  const theme = useTheme();
  const [page, setPage] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);
  const [
    showConfirmUserDeleteDialogForUser,
    setShowConfirmUserDeleteDialogForUser,
  ] = React.useState<OrganizationUser | null>(null);
  const [showManageRolesDialogForUser, setShowManageRolesDialogForUser] =
    React.useState<OrganizationUser | null>(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["User Management", page, rowsPerPage],
    queryFn: () =>
      userManagementService.fetchOrganizationUsers({
        page,
        perPage: rowsPerPage,
      }),
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["set-is-user-active"],
    mutationFn: (payload: SetUserActivePayload) =>
      userManagementService.setIsUserActive(payload),
  });

  const handleSetIsUserActive = async (payload: SetUserActivePayload) => {
    await mutateAsync(payload);
  };

  const { mutateAsync: deleteUserMutateAsync } = useMutation({
    mutationKey: ["delete-user"],
    mutationFn: (userId: string) => userManagementService.deleteUser(userId),
  });

  const handleDeleteUser = async (userId: string) => {
    setShowConfirmUserDeleteDialogForUser(null);
    await deleteUserMutateAsync(userId);
    refetch();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();
      const elapsed = Date.now() - startTime;
      if (elapsed < 500) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 500 - elapsed);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const rows =
    data?.users?.map((user: OrganizationUser) =>
      createData(
        user.id,
        user.email,
        user.first_name,
        user.last_name,
        user.is_active,
        formatDateAndTime(user.created_at),
        user.roles,
      ),
    ) || [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const totalItems = data?.pagination?.total_items || 0;

  return (
    <>
      {showConfirmUserDeleteDialogForUser && (
        <ConfirmUserDeleteDialog
          user={showConfirmUserDeleteDialogForUser}
          open={!!showConfirmUserDeleteDialogForUser}
          handleClose={() => setShowConfirmUserDeleteDialogForUser(null)}
          handleConfirm={() =>
            handleDeleteUser(showConfirmUserDeleteDialogForUser.id)
          }
        />
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box sx={{ flex: 1 }} />

        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            size="small"
            sx={{
              width: 36,
              height: 36,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: "#2563eb",
              },
              "&:disabled": { backgroundColor: "#f9fafb" },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: 18,
                animation: isRefreshing ? "spin 0.8s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>

        <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
          <ButtonView
            type="button"
            variant="contained"
            size="small"
            label="Add User"
            startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
            handleOnClick={handleOpenAddUserDialog}
            sx={{
              backgroundColor: "#2563eb",
              borderRadius: "8px",
              px: 2,
              py: 0.75,
              fontSize: "13px",
              fontWeight: 600,
              height: 36,
              whiteSpace: "nowrap",
              boxShadow: "0 1px 3px rgba(37, 99, 235, 0.3)",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          />
        </RoleBasedAccess>
      </Box>

      {/* Table card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}
          >
            All Users
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}
          >
            {totalItems} users total
          </Typography>
        </Box>

        {isLoading || isRefreshing ? (
          <TableContainer>
            <TableSkeletonLoader />
          </TableContainer>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <TableView
                columns={columns}
                rows={rows}
                handleCustomEvent={async (
                  event: string,
                  value?: string,
                ): Promise<void> => {
                  switch (event) {
                    case "set_is_active": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser) {
                        await handleSetIsUserActive({
                          id: selectedUser.id,
                          isActive: !selectedUser.is_active,
                        });
                        refetch();
                      }
                      break;
                    }
                    case "edit_roles": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser)
                        setShowManageRolesDialogForUser(selectedUser);
                      break;
                    }
                    case "delete_user": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser)
                        setShowConfirmUserDeleteDialogForUser(selectedUser);
                      break;
                    }
                    default:
                      break;
                  }
                }}
                handleClick={() => {}}
                TableList={UserRow}
              />
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                "& .MuiTablePagination-toolbar": { minHeight: 48 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  { fontSize: "12px" },
              }}
            />
          </>
        )}
      </Paper>

      {!!showManageRolesDialogForUser && (
        <ManageRolesDialog
          open={!!showManageRolesDialogForUser}
          handleClose={() => setShowManageRolesDialogForUser(null)}
          handleRoleUpdate={refetch}
          user={showManageRolesDialogForUser}
        />
      )}
    </>
  );
}
